// A confirmation or a rejection applied to the billing map the page already holds, in place of reading
// the list again: module 04 DESIGN B395's pattern, which that entry left the Billing tab out of. The
// list reads twelve months of the Anaplan worksheet for its evidence column (about 0.45 s as the app
// role on the real copy, after migration 106), and the confirmation run is one press for each of about
// 150 rows; nothing in that read changes when a row is decided except the row.
//
// Nothing here decides anything. The API's answer says what was written on the row (who, when, the
// version) in the list's own words; this puts it on the row, moves the row to where the list would have
// put it, and counts again by the API's own rule (apps/api/src/routes/billing-map.ts).
export interface BillingMapRow {
  id: string; organisationId: string; organisationName: string; customer: string; operator: string | null;
  proposedName: string | null; matchedOn: string | null; basis: string | null; proposedAt: string | null;
  confirmedBy: string | null; confirmedAt: string | null; rejectedAt: string | null; version: number;
  /** Module 04 D250: when Cobrain linked this customer itself, on an exact name, and why. Absent from an older API; kept after a person undoes the link. */
  cobrainLinkedAt?: string | null; cobrainReason?: string | null;
  latestContractEnd: string | null; monthsTillContractEnd: number | null; billedLast12: number | null;
  state: 'candidate' | 'confirmed' | 'rejected';
}
export interface BillingMapView { counts: { customers: number; linked: number; candidates: number; rejected: number }; rows: BillingMapRow[] }

/** A link Cobrain made itself and nobody has undone: confirmed, with no person behind it (D250). It is the one kind of confirmation a person's Reject undoes. */
export function linkedByCobrain(r: Pick<BillingMapRow, 'state' | 'confirmedBy' | 'cobrainLinkedAt'>): boolean {
  return r.state === 'confirmed' && !r.confirmedBy && !!r.cobrainLinkedAt;
}

export interface BillingDecided { verb: string; id: string; version: number; confirmedBy: string | null; confirmedAt: string | null; rejectedAt: string | null }

/** Whether the API's answer carries the decision. One from an older API does not, and the page reads the list again instead. */
export function carriesDecision(d: Partial<BillingDecided> | undefined): d is BillingDecided {
  return !!d && typeof d.id === 'string' && (typeof d.confirmedAt === 'string' || typeof d.rejectedAt === 'string');
}

/** The list's own order (billing/store.pg.ts): what needs a person first, then by what the customer was billed, the company, the customer. */
function after(a: BillingMapRow, b: BillingMapRow): boolean {
  const decided = (r: BillingMapRow): number => (r.state === 'candidate' ? 0 : 1);
  if (decided(a) !== decided(b)) return decided(a) > decided(b);
  const billed = (r: BillingMapRow): number => r.billedLast12 ?? 0;
  if (billed(a) !== billed(b)) return billed(a) < billed(b);
  if (a.organisationName !== b.organisationName) return a.organisationName > b.organisationName;
  return a.customer > b.customer;
}

export function applyBillingDecision(view: BillingMapView, d: BillingDecided): BillingMapView {
  const row = view.rows.find((r) => r.id === d.id);
  if (!row) return view;
  const next: BillingMapRow = {
    ...row, version: d.version, confirmedBy: d.confirmedBy, confirmedAt: d.confirmedAt, rejectedAt: d.rejectedAt,
    state: d.confirmedAt ? 'confirmed' : d.rejectedAt ? 'rejected' : 'candidate',
  };
  const rest = view.rows.filter((r) => r.id !== d.id);
  const at = rest.findIndex((r) => after(r, next));
  const rows = at === -1 ? [...rest, next] : [...rest.slice(0, at), next, ...rest.slice(at)];
  return {
    rows,
    counts: {
      customers: rows.length,
      linked: new Set(rows.filter((r) => r.state === 'confirmed').map((r) => r.organisationId)).size,
      candidates: rows.filter((r) => r.state === 'candidate').length,
      rejected: rows.filter((r) => r.state === 'rejected').length,
    },
  };
}
